import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import type { AppLocale } from '@/i18n/config';
import { formatAmountMinor, formatImportCompletedAt } from '../finance-format';
import type { FinanceDashboard } from './dashboard-model';

type DashboardSummaryCardsProps = {
  dashboard: FinanceDashboard;
  selectedYear: number;
  locale: AppLocale;
};

function YearScopedKpi({
  title,
  amountMinor,
  selectedYear,
  locale,
}: {
  title: string;
  amountMinor: number;
  selectedYear: number;
  locale: AppLocale;
}) {
  const { t } = useTranslation('finance');

  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
      </CardHeader>
      <CardContent>
        <p className="card-value">{formatAmountMinor(amountMinor, locale)}</p>
        <p className="card-detail">{t('dashboard.kpis.scopeYear', { year: selectedYear })}</p>
      </CardContent>
    </Card>
  );
}

export function DashboardKpis({ dashboard, selectedYear, locale }: DashboardSummaryCardsProps) {
  const { t } = useTranslation('finance');

  return (
    <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
      <YearScopedKpi
        title={t('dashboard.kpis.inflow')}
        amountMinor={dashboard.totals.inflowMinor}
        selectedYear={selectedYear}
        locale={locale}
      />
      <YearScopedKpi
        title={t('dashboard.kpis.outflow')}
        amountMinor={dashboard.totals.outflowMinor}
        selectedYear={selectedYear}
        locale={locale}
      />
      <YearScopedKpi
        title={t('dashboard.kpis.net')}
        amountMinor={dashboard.totals.netMinor}
        selectedYear={selectedYear}
        locale={locale}
      />
      <Card>
        <CardHeader>
          <CardTitle>{t('dashboard.kpis.cumulative')}</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="card-value">{formatAmountMinor(dashboard.cumulativeMinor, locale)}</p>
          {/*
            This qualifier is the only place the invariance decision is visible on screen. Three
            tiles are scoped to the selected year and this one spans the whole ledger from
            inception; without saying so, four unqualified labels sit side by side and a reader
            has no way to tell which figure answers which question.
          */}
          <p className="card-detail">{t('dashboard.kpis.scopeLedger')}</p>
        </CardContent>
      </Card>
    </section>
  );
}

export function BaselineImportCard({
  baselineImport,
  locale,
}: {
  baselineImport: FinanceDashboard['baselineImport'];
  locale: AppLocale;
}) {
  const { t } = useTranslation('finance');

  return (
    <section aria-labelledby="finance-baseline-import-heading">
      <Card>
        <CardHeader>
          <CardTitle id="finance-baseline-import-heading">
            {t('dashboard.baselineImport.title')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {baselineImport === null ? (
            <p className="card-detail">{t('dashboard.baselineImport.none')}</p>
          ) : (
            <p className="card-detail">
              {/*
                The outcome is words, not a coloured dot, and the status is mapped to a
                business-readable sentence rather than passed through: `hash-mismatch` and
                `rolled-back` are the import's internal taxonomy and mean nothing to the reader
                of a cash-flow screen.
              */}
              {t(`dashboard.baselineImport.status.${baselineImport.status}`)}
              {' — '}
              {t('dashboard.baselineImport.completedAt', {
                timestamp: formatImportCompletedAt(baselineImport.completedAt, locale),
              })}
            </p>
          )}
        </CardContent>
      </Card>
    </section>
  );
}
